import React, { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "./entity-map.css";

// Center on the Philippines — Pasig (~Metro Manila) is a reasonable default
const PH_CENTER = [12.8797, 121.7740];
const PH_DEFAULT_ZOOM = 6;

const TYPE_COLOR = {
  company: "#064E3B",
  site: "#10B981",
  facility: "#F59E0B",
};

const TYPE_RADIUS = {
  company: 10,
  site: 7,
  facility: 5,
};

function fmtAddress(a) {
  if (!a) return "—";
  return [a.street, a.barangay, a.city, a.province, a.country].filter(Boolean).join(", ");
}

export default function EntityMap({ entities = [], onSelect }) {
  const placed = useMemo(
    () => entities.filter((e) => Number.isFinite(e.latitude) && Number.isFinite(e.longitude)),
    [entities]
  );

  // Auto-fit center if a single cluster
  const center = useMemo(() => {
    if (placed.length === 0) return PH_CENTER;
    const lat = placed.reduce((s, e) => s + e.latitude, 0) / placed.length;
    const lng = placed.reduce((s, e) => s + e.longitude, 0) / placed.length;
    return [lat, lng];
  }, [placed]);

  return (
    <div className="relative" data-testid="entity-map">
      <MapContainer
        center={placed.length ? center : PH_CENTER}
        zoom={placed.length ? 8 : PH_DEFAULT_ZOOM}
        style={{ height: 420, width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {placed.map((e) => (
          <CircleMarker
            key={e.id}
            center={[e.latitude, e.longitude]}
            radius={TYPE_RADIUS[e.type] || 6}
            pathOptions={{
              color: TYPE_COLOR[e.type] || "#475569",
              fillColor: TYPE_COLOR[e.type] || "#475569",
              fillOpacity: 0.75,
              weight: 2,
            }}
            eventHandlers={onSelect ? { click: () => onSelect(e) } : undefined}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
              <div className="text-xs">
                <div className="font-semibold">{e.name}</div>
                <div className="text-ink-soft">{e.type}</div>
              </div>
            </Tooltip>
            <Popup className="entity-popup">
              <div className="space-y-1">
                <div className="font-semibold text-sm">{e.name}</div>
                <div className="text-[11px] uppercase tracking-wider text-ink-soft">
                  {e.type}{e.type === "company" && e.ownership_pct != null ? ` · ${e.ownership_pct}% owned` : ""}
                </div>
                <div className="text-[11px]">{fmtAddress(e.address)}</div>
                {e.type === "site" && (
                  <div className="text-[11px] text-ink-soft pt-1 border-t border-border mt-1">
                    {e.headcount ? `${e.headcount.toLocaleString()} FTE · ` : ""}
                    {e.seat_count ? `${e.seat_count.toLocaleString()} seats · ` : ""}
                    {e.floor_area_sqm ? `${e.floor_area_sqm.toLocaleString()} m²` : ""}
                    {e.shift_pattern ? <div className="mt-0.5">{e.shift_pattern}</div> : null}
                  </div>
                )}
                {e.consolidation_factor != null && (
                  <div className="text-[10px] text-ink-soft mt-1 pt-1 border-t border-border">
                    Consolidation factor:{" "}
                    <span className="font-mono">{(e.consolidation_factor * 100).toFixed(1)}%</span>
                  </div>
                )}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur border border-border rounded-md px-3 py-2 text-[11px] shadow-card z-[400]">
        <div className="font-semibold mb-1">Legend</div>
        {[
          ["company", "Company"],
          ["site", "Site"],
          ["facility", "Facility"],
        ].map(([k, label]) => (
          <div key={k} className="flex items-center gap-2">
            <span
              style={{ background: TYPE_COLOR[k] }}
              className="inline-block rounded-full w-2.5 h-2.5"
            />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
